import type { ProductTypeFieldType } from '@emporion/config/product-types'

export type CatalogPluginFieldType = ProductTypeFieldType

export type CatalogCustomFieldValue = string | number | boolean | string[] | null
export type CatalogCustomFields = Record<string, CatalogCustomFieldValue>
export type CatalogProductFieldZone = 'summary' | 'highlights' | 'specs' | 'details' | 'sidebar';

export type CatalogProductLayoutSection = {
  id: string
  title: string
  zone: CatalogProductFieldZone
  layoutType?: 'stack' | 'spec-table' | 'accordion' | 'rich-text' | 'two-column' | 'grid' | 'list'
  order: number
}

export type CatalogProductFieldDefinition = {
  pluginId: string
  productType: string
  id: string
  key: string
  label: string
  type: CatalogPluginFieldType
  unit?: string
  rendererKey?: string
  required: boolean
  options: Array<{ value: string; label: string }>
  display?: {
    showInSummary?: boolean
    showInSpecs?: boolean
    unitPlacement?: 'prefix' | 'suffix'
    order?: number
  }
  search?: {
    searchable?: boolean
    filterable?: boolean
    sortable?: boolean
    facetable?: boolean
  }
  storefront: {
    zone: CatalogProductFieldZone
    order: number
    renderer?: string
    sectionId?: string
  }
  layoutSection?: CatalogProductLayoutSection
}

const ALLOWED_TYPES = new Set<CatalogPluginFieldType>([
  'text',
  'textarea',
  'number',
  'boolean',
  'select',
  'richtext',
  'media',
  'date',
])

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length ? value.trim() : undefined

const asNumber = (value: unknown): number | undefined => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const normalizeStorefrontZone = (value: unknown): CatalogProductFieldZone => {
  const raw = asString(value)
  if (
    raw === 'summary' ||
    raw === 'highlights' ||
    raw === 'specs' ||
    raw === 'details' ||
    raw === 'sidebar'
  ) {
    return raw
  }
  return 'details'
}

const normalizeLayoutType = (
  value: unknown,
): CatalogProductLayoutSection['layoutType'] | undefined => {
  const raw = asString(value)
  if (
    raw === 'stack' ||
    raw === 'spec-table' ||
    raw === 'accordion' ||
    raw === 'rich-text' ||
    raw === 'two-column' ||
    raw === 'grid' ||
    raw === 'list'
  ) {
    return raw
  }
  return undefined
}

const normalizeLayoutSection = (value: unknown): CatalogProductLayoutSection | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const raw = value as Record<string, unknown>
  const id = asString(raw.id)
  const title = asString(raw.title)
  if (!id || !title) return undefined
  return {
    id,
    title,
    zone: normalizeStorefrontZone(raw.zone),
    layoutType: normalizeLayoutType(raw.layoutType),
    order: asNumber(raw.order) ?? 100,
  }
}

export const normalizeCatalogCustomFields = (value: unknown): CatalogCustomFields => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const raw = value as Record<string, unknown>
  const next: CatalogCustomFields = {}
  Object.entries(raw).forEach(([key, fieldValue]) => {
    const normalizedKey = key.trim()
    if (!normalizedKey) return
    if (fieldValue === null) {
      next[normalizedKey] = null
      return
    }
    if (typeof fieldValue === 'string') {
      next[normalizedKey] = fieldValue
      return
    }
    if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
      next[normalizedKey] = fieldValue
      return
    }
    if (typeof fieldValue === 'boolean') {
      next[normalizedKey] = fieldValue
      return
    }
    if (Array.isArray(fieldValue)) {
      next[normalizedKey] = fieldValue
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean)
      return
    }
  })
  return next
}

export const normalizeCatalogProductFieldDefinition = (
  rawField: unknown,
): CatalogProductFieldDefinition | null => {
  if (!rawField || typeof rawField !== 'object') return null
  const raw = rawField as Record<string, unknown>

  const pluginId = asString(raw.pluginId)
  const productTypeRaw = asString(raw.productType)
  const id = asString(raw.id)
  const key = asString(raw.key)
  const label = asString(raw.label)
  const typeRaw = asString(raw.type)
  if (
    !pluginId ||
    !productTypeRaw ||
    !id ||
    !key ||
    !label ||
    !typeRaw ||
    !ALLOWED_TYPES.has(typeRaw as CatalogPluginFieldType)
  ) {
    return null
  }
  const productType = productTypeRaw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  if (!productType) return null

  const options = Array.isArray(raw.options)
    ? raw.options
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return null
          const row = entry as Record<string, unknown>
          const value = asString(row.value)
          if (!value) return null
          return {
            value,
            label: asString(row.label) ?? value,
          }
        })
        .filter((entry): entry is { value: string; label: string } => Boolean(entry))
    : []

  const storefrontRaw =
    raw.storefront && typeof raw.storefront === 'object' ? (raw.storefront as Record<string, unknown>) : {}
  const displayRaw = raw.display && typeof raw.display === 'object' ? (raw.display as Record<string, unknown>) : {}
  const searchRaw = raw.search && typeof raw.search === 'object' ? (raw.search as Record<string, unknown>) : {}
  const zone = normalizeStorefrontZone(storefrontRaw.zone)
  const layoutSection = normalizeLayoutSection(raw.layoutSection)

  return {
    pluginId,
    productType,
    id,
    key,
    label,
    type: typeRaw as CatalogPluginFieldType,
    unit: asString(raw.unit),
    rendererKey: asString(raw.rendererKey),
    required: Boolean(raw.required),
    options,
    display: {
      showInSummary: typeof displayRaw.showInSummary === 'boolean' ? displayRaw.showInSummary : undefined,
      showInSpecs: typeof displayRaw.showInSpecs === 'boolean' ? displayRaw.showInSpecs : undefined,
      unitPlacement: displayRaw.unitPlacement === 'prefix' || displayRaw.unitPlacement === 'suffix' ? displayRaw.unitPlacement : undefined,
      order: asNumber(displayRaw.order),
    },
    search: {
      searchable: typeof searchRaw.searchable === 'boolean' ? searchRaw.searchable : undefined,
      filterable: typeof searchRaw.filterable === 'boolean' ? searchRaw.filterable : undefined,
      sortable: typeof searchRaw.sortable === 'boolean' ? searchRaw.sortable : undefined,
      facetable: typeof searchRaw.facetable === 'boolean' ? searchRaw.facetable : undefined,
    },
    storefront: {
      zone,
      order: asNumber(storefrontRaw.order) ?? 100,
      renderer: asString(storefrontRaw.renderer),
      sectionId: asString(storefrontRaw.sectionId),
    },
    layoutSection,
  }
}
